// Core packages
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { CommunicationsService } from 'src/app/modules/communications/communications.service';
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { UsersService } from 'src/app/modules/users/users.service';
import { ChooseRecipientDialogComponent } from 'src/app/shared/components/dialogs/choose-recipient-dialog/choose-recipient-dialog.component';
import { ConfirmDialogComponent } from 'src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { User } from 'src/app/shared/models/user/user.model';
import { ApiService } from 'src/app/shared/services/api.service';
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Component({
  selector: 'app-send-email-dialog',
  templateUrl: './send-email-dialog.component.html',
  styleUrls: ['./send-email-dialog.component.scss']
})
export class SendEmailDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser = new User();
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  customContactField: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  showBcc = false;
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.send'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-paper-plane',
      inline: true,
    },
  };
  attachmentBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.attach'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'accent',
    spinnerColor: 'accent',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-paperclip',
      inline: true,
    },
  };
  // END - Add communication form

  dialogIsLoading = true;
  previousValue: any = {};
  displayedDocs = [];
  selectedDocs = [];
  preSelectedDocs = [];
  isAdmin = false;
  isMaster = false;
  phonebook = [];
  companyId: string;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<SendEmailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public confirmDialog: MatDialog,
    public chooseRecipientsDialog: MatDialog,
    public configService: ConfigService,

    private cookieService: CookieService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private communicationsService: CommunicationsService,
    private usersService: UsersService,
    private companiesService: CompaniesService,
    private authService: AuthService,
    private helperService: HelperService,
    private apiService: ApiService, // DO NOT MOVE since it's used for 'autocomplete' field type
  ) { }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser.setData(res)));
    if (this.authService.loggedUserRequestedRoleType$.getValue() === "admin"
      || this.authService.loggedUserRequestedRoleType$.getValue() === "operator") {
      this.isAdmin = true;
    }
    if (this.authService.loggedUserRequestedRoleType$.getValue() === "master"
      || this.authService.loggedUserRequestedRoleType$.getValue() === "collaborator") {
      this.isMaster = true;
    }

    // Get current lang
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    // Set company
    this.companyId = this.cookieService.get("requestedCompany");

    // Get form initial values and init the form
    this.getDataAndInitForm();

    // Stop loading dialog
    setTimeout(() => {
      this.dialogIsLoading = false;

    }, 500);

  }

  /**
   * Whether to show the "inserted docs" side area: callers opening the dialog only to send
   * pre-attached files (data.hideInsertedDocs = true) don't need it
   *
   * @since 3.0.0
   */
  get showInsertedDocs(): boolean {
    return !(this.data && this.data.hideInsertedDocs === true);
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }


  /**
   * Preload data and then init form. If something
   * goes wrong just init the form
   *
   * @since 1.0.0
   */
  getDataAndInitForm(): void {
    if (this.data) {
      // Get available docs from insertedDocs, if present
      if (this.data.insertedDocs) {
        // Normalize docs
        this.data.insertedDocs.forEach(docType => {
          if (!docType.data || (docType.data && !docType.data.document)) {
            return;
          }
          for (const doc of docType.data.document) {
            if (doc && !doc.path) {
              return;
            }
            if (!this.model.docs) {
              this.model = {
                ...this.model,
                docs: []
              }
            }
            // Check if doc was pre-selected on dialog open
            let newDoc = {};

            if (
              this.data.selectedDocs
              && this.data.selectedDocs.length
              && this.data.selectedDocs.map(el => el.toString()).includes(docType.docId.toString())
            ) {
              newDoc = {
                key: doc._id,
                value: `${docType.title} - ${doc.originalName}`,
                path: doc.path
              };
              this.model.docs.push(newDoc);
              // newDoc = {
              //   key: doc._id,
              //   value: `${docType.title} - ${doc.originalName}`,
              //   path: doc.path,
              //   selected: true,
              // };
              // this.preSelectedDocs.push(newDoc);
            }
            // else {
            //   newDoc = {
            //     key: doc._id,
            //     value: `${docType.title}`,
            //     path: doc.path
            //   };
            // }
            // this.model.docs.push(newDoc);
          }
        });
      }
    }

    // Pre-attached files (already uploaded through FileService), e.g. a generated document:
    // they become the initial value of the "attachments" file-uploader field
    if (this.data && Array.isArray(this.data.attachments) && this.data.attachments.length) {
      this.model = {
        ...this.model,
        attachments: this.data.attachments,
      };
    }

    // Get available recipients from phonebook
    if (this.isAdmin) {
      this.subscriptions.push(
        this.companiesService.getPhonebookListAdmin(0, null, null, "asc").subscribe(
          res => {
            if (res.status) {
              this.phonebook = res.data.items;
              this.initForm();
            }
          },
          err => {
            this.helperService.handleError(err);
          }
        )
      );
    } else if (this.isMaster) {
      this.subscriptions.push(
        this.companiesService.getPhonebookList(this.companyId, 0, null, null, "asc").subscribe(
          res => {
            if (res.status) {
              // show address associate to property
              if (Array.isArray(res.data.items)) {
                this.phonebook = res.data.items;
                // this.phonebook = res.data.items.filter((item: any) => {
                //   return typeof item.propertyId !== 'undefined' && item.propertyId !== null && typeof item.propertyId._id !== 'undefined' && item.propertyId._id.toString() === this.data.propertyId.toString();
                // });
              }
              this.initForm();
            }
          },
          err => {
            this.helperService.handleError(err);
          }
        )
      );
    }
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Minimum char amount to activate search filtering
    const minTermLength = 1;

    // Set fields
    this.fields = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            key: 'sender',
            hide: true,
            templateOptions: {
              value: this.loggedUser.get("email"),
            },
          },
          {
            className: 'col-11 mb-0 autocomplete-multi-email',
            type: 'autocomplete',
            key: 'recipients',
            templateOptions: {
              customLabel: this.translateService.instant('communications.fields.recipients'),
              required: true,
              bindLabel: 'email',
              bindValue: 'email',
              multiple: true,
              search$: this.isMaster
                ? this.companiesService.getPhonebookListMaster.bind(this)
                : this.companiesService.getPhonebookListAdmin.bind(this),
              hideSelected: true,
              addTag: true,
              minTermLength,
              typeToSearchText: this.translateService.instant('common.words.typeToSearch', { minTermLength }),
              clearAllText: this.translateService.instant('common.words.clearAll'),
              notFoundText: this.translateService.instant('common.words.notFound'),
              addTagText: this.translateService.instant('common.words.addTag'),
              loadingText: this.translateService.instant('common.words.loading'),
            },
            expressionProperties: {
              "templateOptions.onChange": (model) => {
                return model.recipients
              }
            }
          },
          {
            className: 'col-1 mb-0',
            type: 'button',
            key: 'openPhonebook',
            templateOptions: {
              color: 'accent',
              tooltip: 'tooltip',
              icon: {
                fontSet: 'fas',
                fontIcon: 'fa-book-user'
              },
              onClick: ($event) => this.onChooseRecipientDialogOpen('recipients'),
            },
          },
          {
            className: 'col-11 mb-0 autocomplete-multi-email',
            type: 'autocomplete',
            key: 'recipientsCc',
            // hideExpression: (model): boolean => !model.showCc,
            templateOptions: {
              customLabel: this.translateService.instant('communications.fields.recipientsCc'),
              required: false,
              bindLabel: 'email',
              bindValue: 'email',
              multiple: true,
              search$: this.isMaster
                ? this.companiesService.getPhonebookListMaster.bind(this)
                : this.companiesService.getPhonebookListAdmin.bind(this),
              hideSelected: true,
              addTag: true,
              minTermLength,
              typeToSearchText: this.translateService.instant('common.words.typeToSearch', { minTermLength }),
              clearAllText: this.translateService.instant('common.words.clearAll'),
              notFoundText: this.translateService.instant('common.words.notFound'),
              addTagText: this.translateService.instant('common.words.addTag'),
              loadingText: this.translateService.instant('common.words.loading'),
            },
            expressionProperties: {
              "templateOptions.onChange": (model) => {
                return model.recipientsCc
              }
            }
          },
          {
            className: 'col-1 mb-0',
            type: 'button',
            key: 'openPhonebook',
            templateOptions: {
              color: 'accent',
              tooltip: 'tooltip',
              icon: {
                fontSet: 'fas',
                fontIcon: 'fa-book-user'
              },
              onClick: ($event) => this.onChooseRecipientDialogOpen('recipientsCc'),
            },
          },
          {
            className: 'col-11 mb-0 autocomplete-multi-email',
            type: 'autocomplete',
            key: 'recipientsBcc',
            // hideExpression: (model): boolean => !model.showBcc,
            templateOptions: {
              customLabel: this.translateService.instant('communications.fields.recipientsBcc'),
              required: false,
              bindLabel: 'email',
              bindValue: 'email',
              multiple: true,
              search$: this.isMaster
                ? this.companiesService.getPhonebookListMaster.bind(this)
                : this.companiesService.getPhonebookListAdmin.bind(this),
              hideSelected: true,
              addTag: true,
              minTermLength,
              typeToSearchText: this.translateService.instant('common.words.typeToSearch', { minTermLength }),
              clearAllText: this.translateService.instant('common.words.clearAll'),
              notFoundText: this.translateService.instant('common.words.notFound'),
              addTagText: this.translateService.instant('common.words.addTag'),
              loadingText: this.translateService.instant('common.words.loading'),
            },
            expressionProperties: {
              "templateOptions.onChange": (model) => {
                return model.recipientsBcc
              }
            }
          },
          {
            className: 'col-1 mb-0',
            type: 'button',
            key: 'openPhonebook',
            templateOptions: {
              color: 'accent',
              tooltip: 'tooltip',
              icon: {
                fontSet: 'fas',
                fontIcon: 'fa-book-user'
              },
              onClick: ($event) => this.onChooseRecipientDialogOpen('recipientsBcc'),
            },
          },
          {
            className: 'col-12 mb-0',
            template: '<hr>',
          },
          {
            className: 'col-12 pt-2 mb-1',
            type: 'input',
            key: 'subject',
            templateOptions: {
              label: this.translateService.instant('communications.fields.subject'),
              placeholder: this.translateService.instant('communications.fields.subject'),
              required: false,
              minLength: 1,
              maxLength: 100,
            },
          },
          {
            className: 'col-12',
            type: 'textarea',
            key: 'body',
            defaultValue: this.data.body ? this.data.body : "",
            templateOptions: {
              label: this.translateService.instant('communications.fields.body'),
              placeholder: this.translateService.instant('communications.fields.body'),
              required: true,
              maxLength: 5000,
              rows: 6,
            },
          },
          {
            className: "col-12 col-md-6 mb-0",
            type: "file-uploader",
            key: "attachments",
            templateOptions: {
              required: false,
              allowedFilesNumber: 999999,
              allowedMBNumber: 100,
              customLabel: this.translateService.instant('communications.fields.otherFiles'),
            }
          },
        ],
      },
    ];
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    // Prevent unsaved data loss
    if (this.form.dirty && !this.form.pristine) {
      const confirmDialogRef = this.confirmDialog.open(ConfirmDialogComponent, {
        minWidth: '50%',
        maxHeight: '98vh',
        data: {
          title: this.translateService.instant('common.warnings.warning'),
          message: this.translateService.instant('common.warnings.unsavedData'),
          btnOkText: this.translateService.instant('common.cta.continue'),
          btnCancelText: this.translateService.instant('common.cta.undo'),
          // btnCancelIcon: 'fa-times-circle',
        },
        disableClose: true,
      });
      // Subscribe to dialog result (only for 1 emit thanks to "take()")
      confirmDialogRef.beforeClosed()
        .pipe(
          take(1)
        )
        .subscribe(
          res => {
            if (!res) {
              return;
            }
            this.dialogRef.close();
            return;
          }
        );
    } else {
      this.dialogRef.close();
    }
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(model: any): void {
    // One message at a time: «Invia» is off from the click to the answer of the back-end, so a
    // second click can neither open a second confirmation nor send the message twice
    if (this.formBtnOptions.active || this.formBtnOptions.disabled) {
      return;
    }

    // Prevent submit with no recipients
    // const customContactValue = (document.getElementById("customContactField") as HTMLInputElement).value;
    // if (!customContactValue && !this.selectedRecipients.length) {
    //   this.toastrService.warning(this.translateService.instant('common.warnings.no_recipient_selected'));
    //   return;
    // }
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(this.translateService.instant('common.errors.form_is_invalid'));
      return;
    }
    this.formBtnOptions.disabled = true;

    // Set recipients in model
    // this.model = {
    //   ...this.model,
    //   recipients: this.selectedRecipients.map((obj: any) => obj.email),
    // };

    let confirmDialogRef;

    // Email subject is not mandatory, but if it's empty we open a confirm dialog
    // to inform user that there's no subject in mail
    if (!this.form.controls['subject'].value) {
      confirmDialogRef = this.confirmDialog.open(ConfirmDialogComponent, {
        minWidth: '50%',
        maxHeight: '98vh',
        data: {
          title: this.translateService.instant('dialogs.confirm.title'),
          message: this.translateService.instant('communications.add.noSubjectDialog.message'),
          btnOkText: this.translateService.instant('communications.add.noSubjectDialog.confirm'),
          btnOkIcon: 'fa-paper-plane',
          btnCancelText: this.translateService.instant('common.cta.undo'),
          btnCancelIcon: 'fa-times-circle',
        }
      });
    } else {
      // Open confirm dialog before sending email
      confirmDialogRef = this.confirmDialog.open(ConfirmDialogComponent, {
        minWidth: '50%',
        maxHeight: '98vh',
        data: {
          title: this.translateService.instant('dialogs.confirm.title'),
          message: this.translateService.instant('communications.add.confirmMessage'),
          btnOkText: this.translateService.instant('common.cta.send'),
          btnOkIcon: 'fa-paper-plane',
          btnCancelText: this.translateService.instant('common.cta.undo'),
          btnCancelIcon: 'fa-times-circle',
        }
      });
    }


    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    confirmDialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res => {
          if (!res) {
            // Back to main dialog
            this.formBtnOptions.disabled = false;
            return;
          }
          // User confirmed, can send mails
          this.dialogIsLoading = true;
          this.formBtnOptions.active = true;
          this.createCommunication();
        }
      );
  }

  /**
   * Create a new communication and send it
   *
   * @since 1.0.0
   */
  createCommunication() {
    // If message has no subject, let's put a default one before sending
    if (this.model && !this.model.subject) {
      this.model.subject = this.translateService.instant('communications.add.defaultSubject');
    }

    // Handle body formatting (add selectedDocs links). Built on a copy: the text in the dialog stays
    // as typed, so a retry after a failure sends the list once
    let body = this.model.body.concat(`<ul>`);
    // No inserted docs when the dialog is opened with attachments only
    (this.model.docs || []).forEach(doc => {
      if (!doc.path) {
        // This would never happen
        this.toastrService.error('common.errors.unknown');
        return;
      }
      const name = doc.value;
      const path = doc.path.replace(/src[\\\/]assets/g, 'public');
      let url = "";
      if (path.substring(0,22) === "https://app.archedi.it") {
        // url = path;
        const docFilename = path.substr(path.lastIndexOf("/"));
        url = `${this.configService.apiHost}/uploads/properties/${this.data.propertyId}/documents${docFilename}`;
      } else if (/^https?:\/\//i.test(path)) {
        // Already a complete address: prefixing the API host would double it
        url = path;
      } else {
        url = `${this.configService.apiHost}/${path.replace(/^\/+/, '')}`;
      }
      // Names and addresses are stored data: escaped, so that a "&" or a "<" cannot break the list
      const safeName = this.escapeHtml(name);
      const safeUrl = this.escapeHtml(url);
      body = body.concat(`<li>${safeName}: <a href="${safeUrl}">${safeUrl}</a></li><br>`);
    });
    body = body.concat(`</ul>`);

    // Take only emails from recipients
    // let recipients = this.selectedRecipients.map(recipient => recipient.email);

    // Check if we have other addresses
    // const customContactValue = (document.getElementById("customContactField") as HTMLInputElement).value;
    // const otherContacts = customContactValue.split(";").map(el => el.trim());

    // Merge recipients
    // if (otherContacts[0] !== "") {
    //   recipients = [...recipients, ...otherContacts];
    // }

    let newCommunication = {
      recipients: this.model.recipients,
      recipientsCc: null,
      recipientsBcc: null,
      attachments: this.model.attachments,
      subject: this.model.subject,
      body,
      sendDocs: true, // true flag means we're sending docs,
      companyData: this.data.companyData,
      propertyName: this.data.propertyName,
      sentFromSupplierPage: this.data.sentFromSupplierPage,
    }

    if (Array.isArray(this.model.recipientsCc) && this.model.recipientsCc.length > 0) {
      newCommunication = {
        ...newCommunication,
        recipientsCc: this.model.recipientsCc,
      }
    } else {
      delete newCommunication.recipientsCc;
    }

    if (Array.isArray(this.model.recipientsBcc) && this.model.recipientsBcc.length > 0) {
      newCommunication = {
        ...newCommunication,
        recipientsBcc: this.model.recipientsBcc,
      }
    } else {
      delete newCommunication.recipientsBcc;
    }

    this.subscriptions.push(
      this.communicationsService.create(newCommunication)
        .pipe(
          finalize(() => {
            this.formBtnOptions.active = false;
            this.formBtnOptions.disabled = false;
            this.dialogIsLoading = false;
          })
        )
        .subscribe(
          res => {
            if (res.status) {
              this.toastrService.success(res.message);
              this.communicationsService.onCommunicationCreated();
              this.dialogRef.close();
              return;
            }
            this.toastrService.error(this.translateService.instant('common.errors.unknown'));
          },
          err => {
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }

  /**
   * Escape a value written into the HTML of the message (a document name or a link)
   *
   * @since 3.0.0
   */
  private escapeHtml(value: unknown): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Update docs array with incoming element
   *
   * @since 1.0.0
   */
  onDocSelected(doc: any, checked: boolean): void {
    if (doc && checked) {
      this.selectedDocs.push(doc);
    } else if (doc && !checked) {
      const index = this.selectedDocs.map((el) => el.key).indexOf(doc.key);
      this.selectedDocs.splice(index, 1);
    }
  }

  /**
   * Update recipients basing on radio buttons changes
   *
   * @since 1.0.0
   */
  onChooseRecipientDialogOpen(key: string): void {
    if (
      !key
      || (!this.phonebook)
      || (!this.phonebook.length)
    ) {
      this.toastrService.error(this.translateService.instant('common.words.display_no_data'));
      return;
    }

    // Open dialog
    const dialogRef = this.chooseRecipientsDialog.open(ChooseRecipientDialogComponent, {
      minWidth: '60%',
      maxHeight: '98vh',
      data: {
        phonebook: this.phonebook,
        model: this.model[key],
      },
    });

    // On dialog closing, let's update model
    this.subscriptions.push(dialogRef.afterClosed()
      .subscribe(
        res => {
          if (res) {
            const phonebookEmails = this.phonebook.map(contact => contact.email);
            const customInsertedEmails = this.model[key]
              ? this.model[key].filter(email => !phonebookEmails.includes(email))
              : [];
            const newData = [...res, ...customInsertedEmails];
            this.model = {
              ...this.model,
              [key]: newData,
            };
          }
        },
        err => {
          this.helperService.handleError(err);
        }
      ));
  }
}
